import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environment/environment';

export interface UserRequest {
  _id: string;
  type: 'Lab' | 'Document' | 'Data';
  status: 'Pending' | 'In Progress' | 'Approved' | 'Rejected';
  priority: 'Low' | 'Medium' | 'High';
  title: string;
  description: string;
  requestBy: {
    _id: string;
    firstName: string;
    lastName: string;
    email?: string;
    collegeName: string;
    department: string;
    graduationStatus: string;
    profilePicture?: string;
    points?: number;
  };
  labDetails?: {
    nature: string;
    needs: string;
    additionalInfo?: string;
  };
  documentDetails?: {
    type: string;
    title: string;
    author: string;
    doi?: string;
    publisher?: string;
    publishedDate?: string;
  };
  dataDetails?: {
    type: string;
    title: string;
    description: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
  count?: number;
  meta?: {
    totalCount: number;
    currentPage: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
    itemsPerPage: number;
  };
}

export interface RequestStatistics {
  overview: {
    totalRequests: number;
    pendingRequests: number;
    inProgressRequests: number;
    approvedRequests: number;
    rejectedRequests: number;
  };
  byType: {
    [key: string]: number;
  };
  byPriority: {
    [key: string]: number;
  };
}

export interface ResearchRequestFilters {
  page?: number;
  limit?: number;
  search?: string;
  type?: 'Lab' | 'Document' | 'Data';
  priority?: 'Low' | 'Medium' | 'High';
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface CreateUserRequest {
  type: 'Lab' | 'Document' | 'Data';
  title?: string;
  description?: string;
  priority?: 'Low' | 'Medium' | 'High';
  
  // Lab request fields
  labNature?: string;
  labNeeds?: string;
  labAdditionalInfo?: string;
  
  // Document request fields
  documentType?: string;
  documentTitle?: string;
  documentAuthor?: string;
  documentDoi?: string;
  documentPublisher?: string;
  documentPublishedDate?: string;
  
  // Data request fields
  dataType?: string;
  dataTitle?: string;
  dataDescription?: string;
}

@Injectable({
  providedIn: 'root'
})
export class UserRequestService {
  private readonly baseUrl = `${environment.apiUrl}/user-request`;

  constructor(private http: HttpClient) {}

  // ===== PUBLIC WEBSITE APIs (No Authentication Required) =====

  /**
   * Get all unfulfilled user requests for website research page
   * @param filters - Query parameters for filtering and pagination
   * @returns Observable of API response with user requests
   */
  getResearchRequests(filters: ResearchRequestFilters = {}): Observable<ApiResponse<UserRequest[]>> {
    let params = new HttpParams();
    
    if (filters.page) params = params.set('page', filters.page.toString());
    if (filters.limit) params = params.set('limit', filters.limit.toString());
    if (filters.search) params = params.set('search', filters.search);
    if (filters.type) params = params.set('type', filters.type);
    if (filters.priority) params = params.set('priority', filters.priority);
    if (filters.sortBy) params = params.set('sortBy', filters.sortBy);
    if (filters.sortOrder) params = params.set('sortOrder', filters.sortOrder);

    return this.http.get<ApiResponse<UserRequest[]>>(`${this.baseUrl}/website/research`, { params });
  }

  /**
   * Get request statistics for website dashboard
   * @returns Observable of API response with statistics
   */
  getRequestStatistics(): Observable<ApiResponse<RequestStatistics>> {
    return this.http.get<ApiResponse<RequestStatistics>>(`${this.baseUrl}/website/stats`);
  }

  /**
   * Get specific request details for public view
   * @param id - Request ID
   * @returns Observable of API response with request details
   */
  getRequestById(id: string): Observable<ApiResponse<UserRequest>> {
    return this.http.get<ApiResponse<UserRequest>>(`${this.baseUrl}/website/${id}`);
  }

  // ===== AUTHENTICATED USER APIs (Authentication Required) =====

  /**
   * Create a new user request (requires authentication)
   * @param requestData - Request data
   * @returns Observable of API response with created request
   */
  createRequest(requestData: CreateUserRequest): Observable<ApiResponse<UserRequest>> {
    return this.http.post<ApiResponse<UserRequest>>(`${this.baseUrl}/create`, requestData);
  }

  /**
   * Get all requests created by the authenticated user
   * @param filters - Optional filters
   * @returns Observable of API response with user's requests
   */
  getUserRequests(filters: any = {}): Observable<ApiResponse<UserRequest[]>> {
    return this.http.post<ApiResponse<UserRequest[]>>(`${this.baseUrl}/getAll`, filters);
  }

  /**
   * Get user request by ID (authenticated)
   * @param id - Request ID
   * @returns Observable of API response with request details
   */
  getUserRequestById(id: string): Observable<ApiResponse<UserRequest>> {
    return this.http.post<ApiResponse<UserRequest>>(`${this.baseUrl}/getById`, { _id: id });
  }

  /**
   * Update user request (authenticated)
   * @param id - Request ID
   * @param updateData - Data to update
   * @returns Observable of API response with updated request
   */
  updateRequest(id: string, updateData: Partial<CreateUserRequest>): Observable<ApiResponse<UserRequest>> {
    return this.http.post<ApiResponse<UserRequest>>(`${this.baseUrl}/update`, { _id: id, ...updateData });
  }

  /**
   * Delete user request (authenticated)
   * @param id - Request ID
   * @returns Observable of API response
   */
  deleteRequest(id: string): Observable<ApiResponse<null>> {
    return this.http.post<ApiResponse<null>>(`${this.baseUrl}/delete`, { _id: id });
  }

  /**
   * Get user's request statistics (authenticated)
   * @returns Observable of API response with user's statistics
   */
  getUserStatistics(): Observable<ApiResponse<RequestStatistics>> {
    return this.http.post<ApiResponse<RequestStatistics>>(`${this.baseUrl}/getStatistics`, {});
  }

  /**
   * Update request status - Admin only (authenticated)
   * @param id - Request ID
   * @param status - New status
   * @param responseMessage - Optional response message
   * @returns Observable of API response with updated request
   */
  updateRequestStatus(id: string, status: string, responseMessage?: string): Observable<ApiResponse<UserRequest>> {
    return this.http.post<ApiResponse<UserRequest>>(`${this.baseUrl}/updateStatus`, {
      _id: id,
      status,
      responseMessage
    });
  }

  /**
   * Search user requests (authenticated)
   * @param query - Search query
   * @returns Observable of API response with search results
   */
  searchRequests(query: string): Observable<ApiResponse<UserRequest[]>> {
    return this.http.post<ApiResponse<UserRequest[]>>(`${this.baseUrl}/search`, { query });
  }
} 